import React, { useState } from 'react';
import Icon from './Icon';

const PLANS = [
    {
        id: 'tm',
        name: 'T&M',
        price: 299,
        description: 'For contractors auditing T&M invoices',
        badge: null,
        features: [
            'Unlimited T&M audits',
            '110+ T&M audit rules',
            'Schedule A/B/C/D/E compliance',
            'Labor, equipment & consumable checks',
            'Documentation gap detection',
            'Full audit history',
            'PDF export',
        ],
        cta: 'Start T&M',
        highlighted: false,
    },
    {
        id: 'xactimate',
        name: 'Xactimate',
        price: 399,
        description: 'For contractors and adjusters auditing Xactimate estimates',
        badge: null,
        features: [
            'Unlimited Xactimate audits',
            '329+ Xactimate audit rules',
            'Water, fire, mold, roofing & construction',
            'Unbilled opportunity detection',
            'Adjuster flag preparation',
            'Full audit history',
            'PDF export',
        ],
        cta: 'Start Xactimate',
        highlighted: false,
    },
    {
        id: 'all',
        name: 'All Access',
        price: 499,
        description: 'Full platform — Xactimate and T&M combined',
        badge: 'Best Value',
        features: [
            'Everything in T&M + Xactimate',
            '448+ total audit rules',
            'Unlimited audits on all document types',
            'Xactimate estimates',
            'T&M SOC & invoices',
            'Full audit history',
            'PDF export',
        ],
        cta: 'Start All Access',
        highlighted: true,
    },
];

// Paywall copy, keyed on the access_reason the API returns. Each says what
// happened and what happens next; nothing here recomputes access.
function paywallMessage(subscription) {
    const reason = subscription?.access_reason;
    if (reason === 'trial_expired') {
        const ended = subscription.trial_ends_at
            ? ` on ${new Date(subscription.trial_ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            : '';
        return `Your free trial ended${ended}. Your past audits and reports are still here. Choose a plan to run new audits — access starts as soon as checkout completes.`;
    }
    if (reason === 'trial_cap_reached') {
        return `You've used all ${subscription.trial_audit_cap} audits included in your free trial. Your past audits and reports are still here. Choose a plan for unlimited audits — access starts as soon as checkout completes.`;
    }
    if (reason === 'trial') {
        const d = subscription.days_left;
        return `Free trial: ${d} day${d !== 1 ? 's' : ''} left. Subscribe any time to keep auditing after it ends.`;
    }
    return 'Subscribe to unlock unlimited audits.';
}

function PricingScreen({ onBack, onSelectPlan, subscription }) {
    const [loading, setLoading] = useState(null);

    const handleSelect = async (planId) => {
        setLoading(planId);
        await onSelectPlan(planId);
        setLoading(null);
    };

    const blocked = subscription?.access_reason === 'trial_expired' || subscription?.access_reason === 'trial_cap_reached';

    return (
        <div className="pricing-screen">
            <div className="pricing-header">
                <div className="pricing-logo">
                    <span className="logo-audee">Audee<span className="logo-dot">.</span></span>
                </div>
                <div className="pricing-title">Choose your plan</div>
                <div className="pricing-subtitle">
                    {paywallMessage(subscription)}
                </div>
            </div>

            <div className="pricing-cards pricing-cards-3">
                {PLANS.map(plan => (
                    <div key={plan.id} className={"pricing-card " + (plan.highlighted ? "highlighted" : "")}>
                        {plan.badge && <div className="pricing-popular-badge">{plan.badge}</div>}
                        <div className="pricing-plan-name">{plan.name}</div>
                        <div className="pricing-plan-price">
                            <span className="pricing-dollar">$</span>
                            <span className="pricing-amount">{plan.price}</span>
                            <span className="pricing-period">/mo</span>
                        </div>
                        <div className="pricing-plan-desc">{plan.description}</div>
                        <ul className="pricing-features">
                            {plan.features.map(f => (
                                <li key={f} className="pricing-feature">
                                    <span className="pricing-check"><Icon name="check" size={15} strokeWidth={2.25} /></span>
                                    {f}
                                </li>
                            ))}
                        </ul>
                        <button
                            className={"pricing-cta-btn " + (plan.highlighted ? "primary" : "secondary")}
                            onClick={() => handleSelect(plan.id)}
                            disabled={loading !== null}
                        >
                            {loading === plan.id ? "Redirecting..." : plan.cta}
                        </button>
                    </div>
                ))}
            </div>

            <div className="pricing-footer">
                <div className="pricing-guarantee">Cancel anytime · No contracts · Instant access</div>
                <button className="pricing-back-btn" onClick={onBack}>
                    <Icon name="arrow-left" size={13} /> {blocked ? 'Back to your audits' : subscription?.access_reason === 'trial' ? 'Continue with free trial' : 'Back'}
                </button>
            </div>
        </div>
    );
}

export default PricingScreen;
